import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { CircularProgress, TableCell, TableRow, Typography, useMediaQuery } from "@mui/material";
import type { SxProps } from "@mui/material";
import { grey } from "@mui/material/colors";
import BarcodeTypography from "components/atoms/BarcodeTypography";
import StatutButtonGroup from "components/atoms/buttons/StatutButtonGroup/StatutButtonGroup";
import { magasinierArticleStatutOptions } from "constants/SatutMap";
import Statut from "constants/Statut";
import type CommandeArticleTri from "models/CommandeArticleTri";
import type PatchData from "models/PatchData";
import { dateUtil } from "@sdeapps/react-core";
import type { User } from "@sdeapps/react-core";
import commandesService from "services/commandesService";
import ArticleNumeroBonDeCommandePopup from "../ArticleNumeroBonDeCommandePopup";

type TriByArticleLineProps = {
  article: CommandeArticleTri;
  agent?: User;
  disableOnSending?: boolean;
  onNumeroBonDeCommandeChange: (commandeArticle: CommandeArticleTri) => void;
};

function TriByArticleLine({
  article,
  agent,
  disableOnSending = true,
  onNumeroBonDeCommandeChange,
}: Readonly<TriByArticleLineProps>): ReactNode {
  const [workingArticle, setWorkingArticle] = useState<CommandeArticleTri>();
  const [numeroBonDeCommandeDialogOpen, setNumeroBonDeCommandeDialogOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const isLargeScreen = useMediaQuery((theme) => theme.breakpoints.down("xl"));
  const isMediumScreen = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  useEffect(() => {
    setWorkingArticle(article);
    setIsSending(false);
  }, [article]);

  function onStatutChange(statutArticle?: Statut, numeroBonDeCommande?: string): void {
    if (statutArticle == null) return;

    // Cas particulier du statut COMMANDE, un numéro bon de commande est requis
    if (statutArticle === Statut.COMMANDE && (numeroBonDeCommande ?? "").trim() === "") {
      setNumeroBonDeCommandeDialogOpen(true);
      return;
    }

    if (workingArticle != null) {
      setWorkingArticle({
        ...workingArticle,
        statutArticle,
        ...(numeroBonDeCommande != null && { numeroBonDeCommande }),
      });
    }
    void sendCommandeArticleUpdate(statutArticle, numeroBonDeCommande);
  }

  async function sendCommandeArticleUpdate(
    newStatut: Statut,
    numeroBonDeCommande?: string
  ): Promise<void> {
    if (workingArticle == null) {
      console.error("workingArticle is null (TriByArticleLine sendCommandeArticleUpdate)");
      return;
    }
    setIsSending(true);

    const articlePatches: Array<PatchData> = [
      {
        op: "replace",
        path: `/articles/${workingArticle.commandeArticleNumber}/statutArticle`,
        value: newStatut,
      },
    ];
    const hasNumeroBonDeCommandeChanged =
      newStatut === Statut.COMMANDE && (numeroBonDeCommande ?? "").trim() !== "";
    if (hasNumeroBonDeCommandeChanged) {
      articlePatches.push({
        op: "replace",
        path: `/articles/${workingArticle.commandeArticleNumber}/numeroBonDeCommande`,
        value: numeroBonDeCommande,
      });
    }

    try {
      if (articlePatches != null) {
        const updatedCommand = await commandesService.patchCommande(
          workingArticle.commandeId,
          articlePatches
        );
        const articleFound = updatedCommand.articles.find(
          (a) => a.commandeArticleNumber === article.commandeArticleNumber
        );
        if (articleFound != null) {
          const article: CommandeArticleTri = {
            ...articleFound,
            magasinDeRetrait: updatedCommand.magasinDeRetrait,
          };
          setWorkingArticle(article);
          if (
            hasNumeroBonDeCommandeChanged &&
            workingArticle.numeroBonDeCommande !== numeroBonDeCommande
          ) {
            onNumeroBonDeCommandeChange(article);
          }
        }
      } else throw new Error();
      // TODO error handling
    } catch (error) {
      // TODO error handling
      console.error(error);
    } finally {
      setIsSending(false);
    }
  }

  const greyIfSending: SxProps = {
    color: disableOnSending && isSending ? grey[500] : undefined,
  };

  if (workingArticle == null) {
    return null;
  }

  function getStatutButtonCellWidth(): string {
    if (isLargeScreen) {
      if (isMediumScreen) {
        return "290px";
      }
      return "335px";
    }
    return "375px";
  }

  return (
    <TableRow
      sx={{ ".MuiTableCell-root": { paddingX: 1 } }}
      key={`${article.commandeId}${article.commandeArticleNumber}`}>
      <TableCell>
        <Typography sx={greyIfSending}>
          {dateUtil.format(workingArticle.dateCommande, "dd/MM/yyyy")}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={greyIfSending}>
          {agent?.displayName ?? article.demandeurNomComplet ?? "???"}
        </Typography>
      </TableCell>
      <TableCell>
        <BarcodeTypography sx={greyIfSending} variant="barcode1" matricule={agent?.employeeId} />
      </TableCell>
      <TableCell>
        <Typography sx={greyIfSending}>{workingArticle.magasinDeRetrait}</Typography>
      </TableCell>
      <TableCell>
        <Typography sx={greyIfSending}>{workingArticle.quantite}</Typography>
      </TableCell>
      <TableCell>
        <Typography sx={greyIfSending}>
          {workingArticle.taille.toLowerCase() === "taille unique" ? "TU" : workingArticle.taille}
        </Typography>
      </TableCell>
      <TableCell
        sx={{
          "&.MuiTableCell-root": {
            width: getStatutButtonCellWidth(),
            paddingX: 0,
          },
        }}>
        <StatutButtonGroup
          onChangeStatut={onStatutChange}
          value={workingArticle.statutArticle}
          statutOptions={magasinierArticleStatutOptions}
          disabled={disableOnSending && isSending}
          size={isLargeScreen ? "small" : undefined}
          variant={isMediumScreen ? "body2" : undefined}
          smallLabels
        />
        {numeroBonDeCommandeDialogOpen && (
          <ArticleNumeroBonDeCommandePopup
            open={numeroBonDeCommandeDialogOpen}
            onClose={(numero, raison) => {
              if (raison === "OK") {
                onStatutChange(Statut.COMMANDE, numero);
              }

              setNumeroBonDeCommandeDialogOpen(false);
            }}
            actualNumeroBonDeCommande={workingArticle.numeroBonDeCommande}
          />
        )}
      </TableCell>
      <TableCell sx={{ width: "46px" }}>
        {disableOnSending && isSending && <CircularProgress size={30} />}
      </TableCell>
    </TableRow>
  );
}
export default TriByArticleLine;
